import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import RemediationProgressTray from '../../components/RemediationJob/RemediationProgressTray';
import RemediationReviewModal from '../../components/RemediationJob/RemediationReviewModal';
import { useCreateRemediationPlan } from '../../components/services/createRemediationPlan';
import { useVulnerabilityService } from '../../components/services/vulnerabilityService';
import { prepareOsRemediationPlans } from '../../modules/VulnerabilityCenter/Products/osVulnerabilityLogic';
import {
  createRemediationPlans,
  pluralizeDevices,
  prepareRemediationPlans,
} from '../../modules/VulnerabilityCenter/Products/patchifiLogic';
import { RemediationItemProgress, RemediationPlanDraft, Vulnerability } from '../../modules/VulnerabilityCenter/Utils/types';
import { useTenantSelection } from '../TenantSelectionProvider';
import { useToast } from '../ToastProvider/ToastContext';

export type RemediationJobKind = 'software' | 'os';
export type RemediationJobPhase = 'checking' | 'review' | 'creating' | 'done';

export interface RemediationJob {
  tenantId: string;
  kind: RemediationJobKind;
  phase: RemediationJobPhase;
  checkItems: RemediationItemProgress[];
  drafts: RemediationPlanDraft[];
  planItems: RemediationItemProgress[];
}

interface StartRemediationParams {
  kind: RemediationJobKind;
  tenantId: string;
  vulnerabilities: Vulnerability[];
}

interface RemediationJobContextType {
  job: RemediationJob | null;
  isRemediationRunning: boolean;
  startRemediation: (params: StartRemediationParams) => void;
}

const RemediationJobContext = createContext<RemediationJobContextType | null>(null);

export const useRemediationJob = () => {
  const context = useContext(RemediationJobContext);
  if (!context) {
    throw new Error('useRemediationJob must be used within RemediationJobProvider');
  }
  return context;
};

const applyProgress = (items: RemediationItemProgress[], update: RemediationItemProgress) =>
  items.map((item) => (item.key === update.key ? { ...item, ...update } : item));

const isRunning = (job: RemediationJob | null) => !!job && job.phase !== 'done';

const TRAY_AUTO_CLOSE_MS = 5000;

// Runs Vulnerability Center remediation above the router, so it carries on while the user moves
// between pages. One job runs at a time, and it stays on the tenant it started from.
export const RemediationJobProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { addToast } = useToast();
  const { selectedTenantId } = useTenantSelection();
  const { getSoftwareAssetSummary, getSoftwareAssetDevices, getOsUpdateAgents } = useVulnerabilityService();
  const { createRemediationPlan } = useCreateRemediationPlan();

  const [job, setJob] = useState<RemediationJob | null>(null);
  // The ref is what the long-running work reads, as it outlives the render that started it.
  const jobRef = useRef<RemediationJob | null>(null);
  const runIdRef = useRef(0);
  const createPlanRef = useRef(createRemediationPlan);
  createPlanRef.current = createRemediationPlan;

  const updateJob = useCallback((update: (current: RemediationJob) => RemediationJob | null) => {
    if (!jobRef.current) return;
    jobRef.current = update(jobRef.current);
    setJob(jobRef.current);
  }, []);

  const startRemediation = ({ kind, tenantId, vulnerabilities }: StartRemediationParams) => {
    if (isRunning(jobRef.current)) return;

    const runId = ++runIdRef.current;
    const stillCurrent = () => runIdRef.current === runId;

    jobRef.current = {
      tenantId,
      kind,
      phase: 'checking',
      checkItems: vulnerabilities.map((vulnerability) => ({
        key: vulnerability.rowKey,
        title: vulnerability.title || vulnerability.cveId || 'Unknown vulnerability',
        cveId: vulnerability.cveId,
        stage: 'pending',
      })),
      drafts: [],
      planItems: [],
    };
    setJob(jobRef.current);

    const options = {
      vulnerabilities,
      selectedTenantId: tenantId,
      shouldStop: () => !stillCurrent(),
      onProgress: (update: RemediationItemProgress) => {
        if (stillCurrent()) updateJob((current) => ({ ...current, checkItems: applyProgress(current.checkItems, update) }));
      },
    };

    const prepared =
      kind === 'os'
        ? prepareOsRemediationPlans({ ...options, getOsUpdateAgents })
        : prepareRemediationPlans({ ...options, getSoftwareAssetSummary, getSoftwareAssetDevices });

    prepared
      .then((drafts) => {
        if (!stillCurrent()) return;

        if (drafts.length === 0) {
          updateJob((current) => ({ ...current, phase: 'done' }));
          const reasons = Array.from(new Set(jobRef.current?.checkItems.map((item) => item.message).filter(Boolean)));
          addToast(`Nothing needs updating for what you selected. ${reasons.join(', ')}.`, 'info', 6000, 'No remediation plans needed');
          return;
        }

        updateJob((current) => ({ ...current, drafts, phase: 'review' }));
      })
      .catch((error) => {
        console.error('Error preparing remediation plans:', error);
        if (!stillCurrent()) return;
        updateJob((current) => ({ ...current, phase: 'done' }));
        addToast('We could not finish checking your selection. Please try again.', 'error', 6000, 'Remediation stopped');
      });
  };

  const cancelRemediation = useCallback(() => {
    runIdRef.current += 1;
    jobRef.current = null;
    setJob(null);
  }, []);

  const confirmPlans = async (drafts: RemediationPlanDraft[], isEmergency: boolean) => {
    const tenantId = jobRef.current?.tenantId;
    if (!tenantId) return;

    updateJob((current) => ({
      ...current,
      drafts,
      phase: 'creating',
      planItems: drafts.map((draft) => ({ key: draft.key, title: draft.planName, stage: 'pending' })),
    }));

    const { created, failed } = await createRemediationPlans({
      drafts,
      tenantId,
      isEmergency,
      createRemediationPlan: (params) => createPlanRef.current(params),
      onProgress: (update) => updateJob((current) => ({ ...current, planItems: applyProgress(current.planItems, update) })),
    });

    updateJob((current) => ({ ...current, phase: 'done' }));

    const planType = isEmergency ? 'emergency' : 'normal';
    const deviceCount = new Set(drafts.flatMap((draft) => draft.deviceIds)).size;
    const plans = (count: number) => `${count} ${planType} plan${count !== 1 ? 's' : ''}`;

    if (failed === 0) {
      addToast(
        `${plans(created)} created and approved for ${pluralizeDevices(deviceCount)}.`,
        'success',
        6000,
        'Remediation plans created',
      );
    } else if (created > 0) {
      addToast(`${plans(created)} created and approved, ${failed} could not be created.`, 'warning', 6000, 'Some remediation plans failed');
    } else {
      addToast(`None of the ${plans(failed)} could be created. Please try again.`, 'error', 6000, 'Remediation plans failed');
    }
  };

  // Everything is held in this browser tab, so reloading would drop the rest of the job.
  const running = isRunning(job);
  useEffect(() => {
    if (!running) return undefined;

    const warnBeforeLeaving = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };

    window.addEventListener('beforeunload', warnBeforeLeaving);
    return () => window.removeEventListener('beforeunload', warnBeforeLeaving);
  }, [running]);

  const finished = job?.phase === 'done';
  useEffect(() => {
    if (!finished) return undefined;

    const timer = setTimeout(cancelRemediation, TRAY_AUTO_CLOSE_MS);
    return () => clearTimeout(timer);
  }, [finished, cancelRemediation]);

  const value = { job, isRemediationRunning: running, startRemediation };

  return (
    <RemediationJobContext.Provider value={value}>
      {children}
      {job && job.phase !== 'review' && <RemediationProgressTray job={job} onStop={cancelRemediation} onDismiss={cancelRemediation} />}
      {job?.phase === 'review' && (
        <RemediationReviewModal
          job={job}
          isOtherTenantInView={!!selectedTenantId && selectedTenantId !== job.tenantId}
          onCancel={cancelRemediation}
          onConfirm={confirmPlans}
        />
      )}
    </RemediationJobContext.Provider>
  );
};
